import React, { useState, useEffect } from "react";
import "./Borders.css";

const Borders = ({
  selectedElement,
  setSelectedElement,
  updateElementStyles,
  selectedDevice,
  classStylesRegistry,
  setClassStylesRegistry,
  elements,
  setElements,
}) => {
  const [borderMode, setBorderMode] = useState("all");
  const [activeBorderSide, setActiveBorderSide] = useState("all");
  const [radiusMode, setRadiusMode] = useState("all");
  const [activeCorner, setActiveCorner] = useState("topLeft");
  const [borderWidthUnit, setBorderWidthUnit] = useState("px");
  const [borderRadiusUnit, setBorderRadiusUnit] = useState("px");
  const [individualBorderWidthUnits, setIndividualBorderWidthUnits] = useState({
    top: "px",
    right: "px",
    bottom: "px",
    left: "px"
  });
  const [individualBorderRadiusUnits, setIndividualBorderRadiusUnits] = useState({
    topLeft: "px",
    topRight: "px",
    bottomRight: "px",
    bottomLeft: "px"
  });
  const [individualBorderWidths, setIndividualBorderWidths] = useState({
    top: "0",
    right: "0",
    bottom: "0",
    left: "0"
  });
  const [individualBorderStyles, setIndividualBorderStyles] = useState({
    top: "none",
    right: "none",
    bottom: "none",
    left: "none"
  });
  const [individualBorderColors, setIndividualBorderColors] = useState({
    top: "#000000",
    right: "#000000",
    bottom: "#000000",
    left: "#000000"
  });
  const [individualBorderOpacities, setIndividualBorderOpacities] = useState({
    top: "100",
    right: "100",
    bottom: "100",
    left: "100"
  });
  const [individualBorderRadius, setIndividualBorderRadius] = useState({
    topLeft: "0",
    topRight: "0",
    bottomRight: "0",
    bottomLeft: "0"
  });

  // Initialize border mode from selected element
  useEffect(() => {
    if (selectedElement) {
      const borderData = selectedElement.borders?.borderData || {};
      setBorderMode(borderData.mode || "all");
      setRadiusMode(borderData.radiusMode || "all");
      
      // Set main units
      setBorderWidthUnit(borderData.borderWidthUnit || "px");
      setBorderRadiusUnit(borderData.borderRadiusUnit || "px");
      
      // Set individual width units
      setIndividualBorderWidthUnits({
        top: borderData.topWidthBorderUnit || "px",
        right: borderData.rightWidthBorderUnit || "px",
        bottom: borderData.bottomWidthBorderUnit || "px",
        left: borderData.leftWidthBorderUnit || "px"
      });
      
      // Set individual radius units
      setIndividualBorderRadiusUnits({
        topLeft: borderData.topLeftRadiusUnit || "px",
        topRight: borderData.topRightRadiusUnit || "px",
        bottomRight: borderData.bottomRightRadiusUnit || "px",
        bottomLeft: borderData.bottomLeftRadiusUnit || "px"
      });
      
      // Set individual widths
      setIndividualBorderWidths({
        top: borderData.topWidthBorderValue || "0",
        right: borderData.rightWidthBorderValue || "0",
        bottom: borderData.bottomWidthBorderValue || "0",
        left: borderData.leftWidthBorderValue || "0"
      });

      // Set individual styles
      const allModeStyle = borderData.allModeStyleBorderValue;
      const hasAnyStyleValue = allModeStyle || 
                              borderData.topStyleBorderValue || 
                              borderData.rightStyleBorderValue || 
                              borderData.bottomStyleBorderValue || 
                              borderData.leftStyleBorderValue;

      // If in all mode and allModeStyle exists, use it for all sides
      if (borderData.mode === "all" && allModeStyle) {
        setIndividualBorderStyles({
          top: allModeStyle,
          right: allModeStyle,
          bottom: allModeStyle,
          left: allModeStyle
        });
      } else {
        setIndividualBorderStyles({
          top: borderData.topStyleBorderValue || (hasAnyStyleValue ? "solid" : "none"),
          right: borderData.rightStyleBorderValue || (hasAnyStyleValue ? "solid" : "none"),
          bottom: borderData.bottomStyleBorderValue || (hasAnyStyleValue ? "solid" : "none"),
          left: borderData.leftStyleBorderValue || (hasAnyStyleValue ? "solid" : "none")
        });
      }

      // Set individual colors
      setIndividualBorderColors({
        top: borderData.topColorBorderValue || "#000000",
        right: borderData.rightColorBorderValue || "#000000",
        bottom: borderData.bottomColorBorderValue || "#000000",
        left: borderData.leftColorBorderValue || "#000000"
      });

      // Set individual opacities
      setIndividualBorderOpacities({
        top: borderData.topOpacityBorderValue || "100",
        right: borderData.rightOpacityBorderValue || "100",
        bottom: borderData.bottomOpacityBorderValue || "100",
        left: borderData.leftOpacityBorderValue || "100"
      });

      // Initialize border radius
      setIndividualBorderRadius({
        topLeft: borderData.topLeftRadiusValue || "0",
        topRight: borderData.topRightRadiusValue || "0",
        bottomRight: borderData.bottomRightRadiusValue || "0",
        bottomLeft: borderData.bottomLeftRadiusValue || "0"
      });
    }
  }, [selectedElement?.id]);

  // Handle border mode change
  const handleBorderModeChange = (mode) => {
    if (!selectedElement) return;

    setBorderMode(mode);
    
    if (mode === "all") {
      // When switching to "all" mode, use the top border width for all sides
      const newWidth = individualBorderWidths.top || "0";
      const newIndividualWidths = {
        top: newWidth,
        right: newWidth,
        bottom: newWidth,
        left: newWidth
      };
      setIndividualBorderWidths(newIndividualWidths);

      // Update all sides with the same width
      Object.entries(newIndividualWidths).forEach(([side, width]) => {
        const property = `border${side.charAt(0).toUpperCase() + side.slice(1)}Width`;
        updateElementStyles(
          selectedElement.id,
          property,
          `${width}${borderWidthUnit}`,
          null,
          selectedDevice
        );
      });

      // Initialize borders object if it doesn't exist
      const currentBorders = selectedElement.borders || {};
      const currentBorderData = currentBorders.borderData || {};

      // Update selectedElement with new values
      const updatedElement = {
        ...selectedElement,
        borders: {
          ...currentBorders,
          borderData: {
            ...currentBorderData,
            mode,
            allModeWidthBorderValue: newWidth,
            topWidthBorderValue: newWidth,
            rightWidthBorderValue: newWidth,
            bottomWidthBorderValue: newWidth,
            leftWidthBorderValue: newWidth
          }
        }
      };
      
      setSelectedElement(updatedElement);
      setElements(elements.map(element => 
        element.id === selectedElement.id ? updatedElement : element
      ));
    } else {
      // Initialize borders object if it doesn't exist
      const currentBorders = selectedElement.borders || {};
      const currentBorderData = currentBorders.borderData || {};

      // Update selectedElement with border mode only
      const updatedElement = {
        ...selectedElement,
        borders: {
          ...currentBorders,
          borderData: {
            ...currentBorderData,
            mode
          }
        }
      };
      
      setSelectedElement(updatedElement);
      setElements(elements.map(element => 
        element.id === selectedElement.id ? updatedElement : element
      ));
    }
  };

  // Handle border width change for all sides
  const handleBorderWidthChange = (value) => {
    if (!selectedElement) return;
    
    const newWidth = value || "0";
    const sides = ["top", "right", "bottom", "left"];
    
    // Update all sides with the new width
    const newIndividualWidths = sides.reduce((acc, side) => {
      acc[side] = newWidth;
      return acc;
    }, {});
    
    setIndividualBorderWidths(newIndividualWidths);

    // Update styles for all sides
    sides.forEach(side => {
      const property = `border${side.charAt(0).toUpperCase() + side.slice(1)}Width`;
      updateElementStyles(
        selectedElement.id,
        property,
        `${newWidth}${borderWidthUnit}`,
        null,
        selectedDevice
      );
    });

    // Initialize borders object if it doesn't exist
    const currentBorders = selectedElement.borders || {};
    const currentBorderData = currentBorders.borderData || {};

    // Update with new values
    const updatedElement = {
      ...selectedElement,
      borders: {
        ...currentBorders,
        borderData: {
          ...currentBorderData,
          allModeWidthBorderValue: newWidth,
          topWidthBorderValue: newWidth,
          rightWidthBorderValue: newWidth,
          bottomWidthBorderValue: newWidth,
          leftWidthBorderValue: newWidth
        }
      }
    };
    
    setSelectedElement(updatedElement);
    setElements(elements.map(element => 
      element.id === selectedElement.id ? updatedElement : element
    ));
  };

  // Handle individual border width change
  const handleIndividualBorderWidthChange = (side, value) => {
    if (!selectedElement) return;

    const width = value || "0";
    
    // Update local state
    setIndividualBorderWidths(prev => ({
      ...prev,
      [side]: width
    }));

    // Update the specific border width style
    const property = `border${side.charAt(0).toUpperCase() + side.slice(1)}Width`;
    updateElementStyles(
      selectedElement.id,
      property,
      `${width}${individualBorderWidthUnits[side]}`,
      null,
      selectedDevice
    );

    // Initialize borders object if it doesn't exist
    const currentBorders = selectedElement.borders || {};
    const currentBorderData = currentBorders.borderData || {};

    // Update the specific side value
    const valueKey = `${side}WidthBorderValue`;
    const updatedElement = {
      ...selectedElement,
      borders: {
        ...currentBorders,
        borderData: {
          ...currentBorderData,
          [valueKey]: width
        }
      }
    };
    
    setSelectedElement(updatedElement);
    setElements(elements.map(element => 
      element.id === selectedElement.id ? updatedElement : element
    ));
  };

  // Handle border style change for all sides
  const handleBorderStyleChange = (style) => {
    if (!selectedElement) return;
    
    // Update all sides with the new style
    const newIndividualStyles = {
      top: style,
      right: style,
      bottom: style,
      left: style
    };
    
    setIndividualBorderStyles(newIndividualStyles);

    // Update styles for all sides
    ["Top", "Right", "Bottom", "Left"].forEach(side => {
      updateElementStyles(
        selectedElement.id,
        `border${side}Style`,
        style,
        null,
        selectedDevice
      );
    });

    // Initialize borders object if it doesn't exist
    const currentBorders = selectedElement.borders || {};
    const currentBorderData = currentBorders.borderData || {};

    // Update with new values
    const updatedElement = {
      ...selectedElement,
      borders: {
        ...currentBorders,
        borderData: {
          ...currentBorderData,
          allModeStyleBorderValue: style,
          topStyleBorderValue: style,
          rightStyleBorderValue: style,
          bottomStyleBorderValue: style,
          leftStyleBorderValue: style
        }
      }
    };
    
    setSelectedElement(updatedElement);
    setElements(elements.map(element => 
      element.id === selectedElement.id ? updatedElement : element
    ));
  };

  // Handle individual border style change
  const handleIndividualBorderStyleChange = (side, style) => {
    if (!selectedElement) return;
    
    // Update local state
    setIndividualBorderStyles(prev => ({
      ...prev,
      [side]: style
    }));

    // Capitalize first letter for the style property
    const capitalizedSide = side.charAt(0).toUpperCase() + side.slice(1);
    
    // Update the specific border style
    updateElementStyles(
      selectedElement.id,
      `border${capitalizedSide}Style`,
      style,
      null,
      selectedDevice
    );

    // Initialize borders object if it doesn't exist
    const currentBorders = selectedElement.borders || {};
    const currentBorderData = currentBorders.borderData || {};

    // Update the specific side value
    const valueKey = `${side}StyleBorderValue`;
    const updatedElement = {
      ...selectedElement,
      borders: {
        ...currentBorders,
        borderData: {
          ...currentBorderData,
          [valueKey]: style
        }
      }
    };
    
    setSelectedElement(updatedElement);
    setElements(elements.map(element => 
      element.id === selectedElement.id ? updatedElement : element
    ));
  };

  // Handle border color change for all sides
  const handleBorderColorChange = (color) => {
    if (!selectedElement) return;
    
    // Update all sides with the new color
    const sides = ["Top", "Right", "Bottom", "Left"];
    sides.forEach(side => {
      updateElementStyles(
        selectedElement.id,
        `border${side}Color`,
        color,
        null,
        selectedDevice
      );
    });

    // Update local state
    const newColors = {
      top: color,
      right: color,
      bottom: color,
      left: color
    };
    setIndividualBorderColors(newColors);

    // Initialize borders object if it doesn't exist
    const currentBorders = selectedElement.borders || {};
    const currentBorderData = currentBorders.borderData || {};

    // Update with new values
    const updatedElement = {
      ...selectedElement,
      borders: {
        ...currentBorders,
        borderData: {
          ...currentBorderData,
          allModeColorBorderValue: color,
          topColorBorderValue: color,
          rightColorBorderValue: color,
          bottomColorBorderValue: color,
          leftColorBorderValue: color
        }
      }
    };
    
    setSelectedElement(updatedElement);
    setElements(elements.map(element => 
      element.id === selectedElement.id ? updatedElement : element
    ));
  };

  // Handle individual border color change
  const handleIndividualBorderColorChange = (side, color) => {
    if (!selectedElement) return;
    
    // Update local state
    setIndividualBorderColors(prev => ({
      ...prev,
      [side]: color
    }));

    // Capitalize first letter for the style property
    const capitalizedSide = side.charAt(0).toUpperCase() + side.slice(1);
    
    // Update the specific border color
    updateElementStyles(
      selectedElement.id,
      `border${capitalizedSide}Color`,
      color,
      null,
      selectedDevice
    );

    // Initialize borders object if it doesn't exist
    const currentBorders = selectedElement.borders || {};
    const currentBorderData = currentBorders.borderData || {};

    // Update the specific side value
    const valueKey = `${side}ColorBorderValue`;
    const updatedElement = {
      ...selectedElement,
      borders: {
        ...currentBorders,
        borderData: {
          ...currentBorderData,
          [valueKey]: color
        }
      }
    };
    
    setSelectedElement(updatedElement);
    setElements(elements.map(element => 
      element.id === selectedElement.id ? updatedElement : element
    ));
  };

  // Handle border opacity change for all sides
  const handleBorderOpacityChange = (opacity) => {
    if (!selectedElement) return;
    
    // Update all sides with the new opacity
    const sides = ["Top", "Right", "Bottom", "Left"];
    const opacityValue = opacity / 100;
    
    sides.forEach(side => {
      updateElementStyles(
        selectedElement.id,
        `border${side}Color`,
        individualBorderColors.top,
        opacityValue,
        selectedDevice
      );
    });

    // Update local state
    const newOpacities = {
      top: opacity,
      right: opacity,
      bottom: opacity,
      left: opacity
    };
    setIndividualBorderOpacities(newOpacities);

    // Initialize borders object if it doesn't exist
    const currentBorders = selectedElement.borders || {};
    const currentBorderData = currentBorders.borderData || {};

    // Update with new values
    const updatedElement = {
      ...selectedElement,
      borders: {
        ...currentBorders,
        borderData: {
          ...currentBorderData,
          allModeOpacityBorderValue: opacity,
          topOpacityBorderValue: opacity,
          rightOpacityBorderValue: opacity,
          bottomOpacityBorderValue: opacity,
          leftOpacityBorderValue: opacity
        }
      }
    };
    
    setSelectedElement(updatedElement);
    setElements(elements.map(element => 
      element.id === selectedElement.id ? updatedElement : element
    ));
  };

  // Handle individual border opacity change
  const handleIndividualBorderOpacityChange = (side, opacity) => {
    if (!selectedElement) return;
    
    // Update local state
    setIndividualBorderOpacities(prev => ({
      ...prev,
      [side]: opacity
    }));

    // Capitalize first letter for the style property
    const capitalizedSide = side.charAt(0).toUpperCase() + side.slice(1);
    
    // Update the specific border opacity
    updateElementStyles(
      selectedElement.id,
      `border${capitalizedSide}Color`,
      individualBorderColors[side],
      opacity / 100,
      selectedDevice
    );

    // Initialize borders object if it doesn't exist
    const currentBorders = selectedElement.borders || {};
    const currentBorderData = currentBorders.borderData || {};

    // Update the specific side value
    const valueKey = `${side}OpacityBorderValue`;
    const updatedElement = {
      ...selectedElement,
      borders: {
        ...currentBorders,
        borderData: {
          ...currentBorderData,
          [valueKey]: opacity
        }
      }
    };
    
    setSelectedElement(updatedElement);
    setElements(elements.map(element => 
      element.id === selectedElement.id ? updatedElement : element
    ));
  };

  // Handle radius mode change
  const handleRadiusModeChange = (mode) => {
    if (!selectedElement) return;

    setRadiusMode(mode);
    
    if (mode === "all") {
      // When switching to "all" mode, use the top-left radius for all corners
      const newRadius = individualBorderRadius.topLeft || "0";
      const newRadiusValues = {
        topLeft: newRadius,
        topRight: newRadius,
        bottomRight: newRadius,
        bottomLeft: newRadius
      };
      setIndividualBorderRadius(newRadiusValues);

      // Update all corners with the same radius
      updateElementStyles(
        selectedElement.id,
        "borderRadius",
        `${newRadius}${borderRadiusUnit}`,
        null,
        selectedDevice
      );

      // Initialize borders object if it doesn't exist
      const currentBorders = selectedElement.borders || {};
      const currentBorderData = currentBorders.borderData || {};

      // Update selectedElement with new values
      const updatedElement = {
        ...selectedElement,
        borders: {
          ...currentBorders,
          borderData: {
            ...currentBorderData,
            radiusMode: mode,
            allModeRadiusValue: newRadius,
            topLeftRadiusValue: newRadius,
            topRightRadiusValue: newRadius,
            bottomRightRadiusValue: newRadius,
            bottomLeftRadiusValue: newRadius
          }
        }
      };
      
      setSelectedElement(updatedElement);
      setElements(elements.map(element => 
        element.id === selectedElement.id ? updatedElement : element
      ));
    } else {
      // Initialize borders object if it doesn't exist
      const currentBorders = selectedElement.borders || {};
      const currentBorderData = currentBorders.borderData || {};

      // Update selectedElement with radius mode only
      const updatedElement = {
        ...selectedElement,
        borders: {
          ...currentBorders,
          borderData: {
            ...currentBorderData,
            radiusMode: mode
          }
        }
      };
      
      setSelectedElement(updatedElement);
      setElements(elements.map(element => 
        element.id === selectedElement.id ? updatedElement : element
      ));
    }
  };

  // Handle border radius change for all corners
  const handleBorderRadiusChange = (radius) => {
    if (!selectedElement) return;
    
    const newRadius = radius || "0";

    // Update all corners with the new radius
    updateElementStyles(
      selectedElement.id,
      "borderRadius",
      `${newRadius}${borderRadiusUnit}`,
      null,
      selectedDevice
    );

    // Update local state
    const newRadiusValues = {
      topLeft: newRadius,
      topRight: newRadius,
      bottomRight: newRadius,
      bottomLeft: newRadius
    };
    setIndividualBorderRadius(newRadiusValues);

    // Initialize borders object if it doesn't exist
    const currentBorders = selectedElement.borders || {};
    const currentBorderData = currentBorders.borderData || {};

    // Update with new values
    const updatedElement = {
      ...selectedElement,
      borders: {
        ...currentBorders,
        borderData: {
          ...currentBorderData,
          allModeRadiusValue: newRadius,
          topLeftRadiusValue: newRadius,
          topRightRadiusValue: newRadius,
          bottomRightRadiusValue: newRadius,
          bottomLeftRadiusValue: newRadius
        }
      }
    };
    
    setSelectedElement(updatedElement);
    setElements(elements.map(element => 
      element.id === selectedElement.id ? updatedElement : element
    ));
  };

  // Handle individual border radius change
  const handleIndividualBorderRadiusChange = (corner, radius) => {
    if (!selectedElement) return;
    
    const newRadius = radius || "0";
    
    // Update local state
    setIndividualBorderRadius(prev => ({
      ...prev,
      [corner]: newRadius
    }));

    // Create the style property name
    const cornerMap = {
      topLeft: "borderTopLeftRadius",
      topRight: "borderTopRightRadius",
      bottomRight: "borderBottomRightRadius",
      bottomLeft: "borderBottomLeftRadius"
    };
    
    // Update the specific corner radius
    updateElementStyles(
      selectedElement.id,
      cornerMap[corner],
      `${newRadius}${individualBorderRadiusUnits[corner]}`,
      null,
      selectedDevice
    );

    // Initialize borders object if it doesn't exist
    const currentBorders = selectedElement.borders || {};
    const currentBorderData = currentBorders.borderData || {};

    // Update the specific corner value
    const valueKey = `${corner}RadiusValue`;
    const updatedElement = {
      ...selectedElement,
      borders: {
        ...currentBorders,
        borderData: {
          ...currentBorderData,
          [valueKey]: newRadius
        }
      }
    };
    
    setSelectedElement(updatedElement);
    setElements(elements.map(element => 
      element.id === selectedElement.id ? updatedElement : element
    ));
  };

  // Handle border width unit change for all sides
  const handleBorderWidthUnitChange = (unit) => {
    if (!selectedElement) return;
    
    setBorderWidthUnit(unit);
    const width = individualBorderWidths.top || "0";
    const sides = ["top", "right", "bottom", "left"];
    
    // Update all sides with the new unit
    sides.forEach(side => {
      const property = `border${side.charAt(0).toUpperCase() + side.slice(1)}Width`;
      updateElementStyles(
        selectedElement.id,
        property,
        `${width}${unit}`,
        null,
        selectedDevice
      );
    });

    // Initialize borders object if it doesn't exist
    const currentBorders = selectedElement.borders || {};
    const currentBorderData = currentBorders.borderData || {};

    // Set individual width units to the same value
    const newIndividualWidthUnits = {
      top: unit,
      right: unit,
      bottom: unit,
      left: unit
    };
    setIndividualBorderWidthUnits(newIndividualWidthUnits);

    // Update with new values
    const updatedElement = {
      ...selectedElement,
      borders: {
        ...currentBorders,
        borderData: {
          ...currentBorderData,
          borderWidthUnit: unit,
          topWidthBorderUnit: unit,
          rightWidthBorderUnit: unit,
          bottomWidthBorderUnit: unit,
          leftWidthBorderUnit: unit
        }
      }
    };
    
    setSelectedElement(updatedElement);
    setElements(elements.map(element => 
      element.id === selectedElement.id ? updatedElement : element
    ));
  };

  // Handle individual border width unit change
  const handleIndividualBorderWidthUnitChange = (side, unit) => {
    if (!selectedElement) return;
    
    // Update local state
    setIndividualBorderWidthUnits(prev => ({
      ...prev,
      [side]: unit
    }));

    const width = individualBorderWidths[side] || "0";
    
    // Update the specific border width style
    const property = `border${side.charAt(0).toUpperCase() + side.slice(1)}Width`;
    updateElementStyles(
      selectedElement.id,
      property,
      `${width}${unit}`,
      null,
      selectedDevice
    );

    // Initialize borders object if it doesn't exist
    const currentBorders = selectedElement.borders || {};
    const currentBorderData = currentBorders.borderData || {};

    // Update the specific side unit
    const unitKey = `${side}WidthBorderUnit`;
    const updatedElement = {
      ...selectedElement,
      borders: {
        ...currentBorders,
        borderData: {
          ...currentBorderData,
          [unitKey]: unit
        }
      }
    };
    
    setSelectedElement(updatedElement);
    setElements(elements.map(element => 
      element.id === selectedElement.id ? updatedElement : element
    ));
  };

  // Handle border radius unit change for all corners
  const handleBorderRadiusUnitChange = (unit) => {
    if (!selectedElement) return;
    
    setBorderRadiusUnit(unit);
    const radius = individualBorderRadius.topLeft || "0";
    
    // Update all corners with the new unit
    updateElementStyles(
      selectedElement.id,
      "borderRadius",
      `${radius}${unit}`,
      null,
      selectedDevice
    );

    // Initialize borders object if it doesn't exist
    const currentBorders = selectedElement.borders || {};
    const currentBorderData = currentBorders.borderData || {};

    // Set individual radius units to the same value
    const newIndividualRadiusUnits = {
      topLeft: unit,
      topRight: unit,
      bottomRight: unit,
      bottomLeft: unit
    };
    setIndividualBorderRadiusUnits(newIndividualRadiusUnits);

    // Update with new values
    const updatedElement = {
      ...selectedElement,
      borders: {
        ...currentBorders,
        borderData: {
          ...currentBorderData,
          borderRadiusUnit: unit,
          topLeftRadiusUnit: unit,
          topRightRadiusUnit: unit,
          bottomRightRadiusUnit: unit,
          bottomLeftRadiusUnit: unit
        }
      }
    };
    
    setSelectedElement(updatedElement);
    setElements(elements.map(element => 
      element.id === selectedElement.id ? updatedElement : element
    ));
  };

  // Handle individual border radius unit change
  const handleIndividualBorderRadiusUnitChange = (corner, unit) => {
    if (!selectedElement) return;
    
    // Update local state
    setIndividualBorderRadiusUnits(prev => ({
      ...prev,
      [corner]: unit
    }));

    const radius = individualBorderRadius[corner] || "0";
    
    // Create the style property name
    const cornerMap = {
      topLeft: "borderTopLeftRadius",
      topRight: "borderTopRightRadius",
      bottomRight: "borderBottomRightRadius",
      bottomLeft: "borderBottomLeftRadius"
    };
    
    // Update the specific corner radius
    updateElementStyles(
      selectedElement.id,
      cornerMap[corner],
      `${radius}${unit}`,
      null,
      selectedDevice
    );

    // Initialize borders object if it doesn't exist
    const currentBorders = selectedElement.borders || {};
    const currentBorderData = currentBorders.borderData || {};

    // Update the specific corner unit
    const unitKey = `${corner}RadiusUnit`;
    const updatedElement = {
      ...selectedElement,
      borders: {
        ...currentBorders,
        borderData: {
          ...currentBorderData,
          [unitKey]: unit
        }
      }
    };
    
    setSelectedElement(updatedElement);
    setElements(elements.map(element => 
      element.id === selectedElement.id ? updatedElement : element
    ));
  };

  return (
    <div className="borders-section">
      {/* Border Type Row */}
      <div className="border-property">
        <div className="style-label">Type</div>
        <div className="style-controls border-mode-controls">
          <button
            className={`border-mode-button ${borderMode === "all" ? "active" : ""}`}
            onClick={() => handleBorderModeChange("all")}
            title="All Borders"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="18" height="18" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
          </button>
          <button
            className={`border-mode-button ${borderMode === "individual" ? "active" : ""}`}
            onClick={() => handleBorderModeChange("individual")}
            title="Individual Borders"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 3H21M3 21H21M3 3V21M21 3V21" stroke="currentColor" strokeWidth="2" strokeDasharray="2 2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Border Sides Row - only show if individual mode is selected */}
      {borderMode === "individual" && (
        <div className="border-property">
          <div className="style-label">Side</div>
          <div className="style-controls border-sides-controls">
            <button
              className={`border-side-button ${activeBorderSide === "top" ? "active" : ""}`}
              onClick={() => setActiveBorderSide("top")}
              title="Top Border"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 3H21" stroke="currentColor" strokeWidth="2" />
                <path d="M3 21H21M3 3V21M21 3V21" stroke="currentColor" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="2 2" />
              </svg>
            </button>
            <button
              className={`border-side-button ${activeBorderSide === "right" ? "active" : ""}`}
              onClick={() => setActiveBorderSide("right")}
              title="Right Border"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 3V21" stroke="currentColor" strokeWidth="2" />
                <path d="M3 21H21M3 3H21M3 3V21" stroke="currentColor" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="2 2" />
              </svg>
            </button>
            <button
              className={`border-side-button ${activeBorderSide === "bottom" ? "active" : ""}`}
              onClick={() => setActiveBorderSide("bottom")}
              title="Bottom Border"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 21H21" stroke="currentColor" strokeWidth="2" />
                <path d="M3 3H21M3 3V21M21 3V21" stroke="currentColor" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="2 2" />
              </svg>
            </button>
            <button
              className={`border-side-button ${activeBorderSide === "left" ? "active" : ""}`}
              onClick={() => setActiveBorderSide("left")}
              title="Left Border"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 3V21" stroke="currentColor" strokeWidth="2" />
                <path d="M3 21H21M3 3H21M21 3V21" stroke="currentColor" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="2 2" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Width Row */}
      <div className="border-property">
        <div className="style-label">Width</div>
        <div className="style-controls border-width-controls">
          <input
            type="text"
            className="style-input"
            placeholder="0"
            value={borderMode === "all" ? individualBorderWidths.top : individualBorderWidths[activeBorderSide]}
            onChange={(e) => 
              borderMode === "all" 
                ? handleBorderWidthChange(e.target.value)
                : handleIndividualBorderWidthChange(activeBorderSide, e.target.value)
            }
          />
          <div className="unit-dropdown">
            <select
              className="unit-select"
              value={borderMode === "all" ? borderWidthUnit : individualBorderWidthUnits[activeBorderSide]}
              onChange={(e) => 
                borderMode === "all"
                  ? handleBorderWidthUnitChange(e.target.value)
                  : handleIndividualBorderWidthUnitChange(activeBorderSide, e.target.value)
              }
            >
              <option value="" disabled>Unit</option>
              <option value="px">px</option>
              <option value="%">%</option>
              <option value="em">em</option>
              <option value="rem">rem</option>
              <option value="ch">ch</option>
            </select>
          </div>
        </div>
      </div>

      {/* Style Row */}
      <div className="border-property">
        <div className="style-label">Style</div>
        <div className="style-controls border-style-controls">
          <button
            className={`border-style-button${
              (borderMode === "all"
                ? !selectedElement.borders || 
                  !selectedElement.borders?.borderData?.allModeStyleBorderValue ||
                  selectedElement.borders?.borderData?.allModeStyleBorderValue === "none"
                : individualBorderStyles[activeBorderSide] === "none")
              ? " active" : ""
            }`}
            onClick={() => 
              borderMode === "all"
                ? handleBorderStyleChange("none")
                : handleIndividualBorderStyleChange(activeBorderSide, "none")
            }
            title="None"
          >
            <i className="fas fa-minus" style={{ color: "#fff" }}></i>
          </button>
          <button
            className={`border-style-button${
              (borderMode === "all"
                ? selectedElement.borders?.borderData?.allModeStyleBorderValue === "solid"
                : individualBorderStyles[activeBorderSide] === "solid")
              ? " active" : ""
            }`}
            onClick={() => 
              borderMode === "all"
                ? handleBorderStyleChange("solid")
                : handleIndividualBorderStyleChange(activeBorderSide, "solid")
            }
            title="Solid"
          >
            <div className="border-solid-icon"></div>
          </button>
          <button
            className={`border-style-button${
              (borderMode === "all"
                ? selectedElement.borders?.borderData?.allModeStyleBorderValue === "dashed"
                : individualBorderStyles[activeBorderSide] === "dashed")
              ? " active" : ""
            }`}
            onClick={() => 
              borderMode === "all"
                ? handleBorderStyleChange("dashed")
                : handleIndividualBorderStyleChange(activeBorderSide, "dashed")
            }
            title="Dashed"
          >
            <div className="border-dashed-icon"></div>
          </button>
          <button
            className={`border-style-button${
              (borderMode === "all"
                ? selectedElement.borders?.borderData?.allModeStyleBorderValue === "dotted"
                : individualBorderStyles[activeBorderSide] === "dotted")
              ? " active" : ""
            }`}
            onClick={() => 
              borderMode === "all"
                ? handleBorderStyleChange("dotted")
                : handleIndividualBorderStyleChange(activeBorderSide, "dotted")
            }
            title="Dotted"
          >
            <div className="border-dotted-icon"></div>
          </button>
        </div>
      </div>

      {/* Color Row */}
      <div className="border-property">
        <div className="style-label">Color</div>
        <div className="style-controls border-color-controls">
          <input
            type="color"
            className="color-picker"
            value={borderMode === "all" 
              ? selectedElement?.borders?.borderData?.allModeColorBorderValue || individualBorderColors.top
              : individualBorderColors[activeBorderSide]}
            onChange={(e) => 
              borderMode === "all"
                ? handleBorderColorChange(e.target.value)
                : handleIndividualBorderColorChange(activeBorderSide, e.target.value)
            }
          />
          {/* <div className="color-opacity-slider">
            <input
              type="range"
              min="0"
              max="100"
              value={borderMode === "all"
                ? selectedElement?.borders?.borderData?.allModeOpacityBorderValue || individualBorderOpacities.top
                : individualBorderOpacities[activeBorderSide]}
              className="opacity-slider"
              onChange={(e) => 
                borderMode === "all"
                  ? handleBorderOpacityChange(e.target.value)
                  : handleIndividualBorderOpacityChange(activeBorderSide, e.target.value)
              }
            />
            <div className="opacity-value">
              {borderMode === "all"
                ? selectedElement?.borders?.borderData?.allModeOpacityBorderValue || individualBorderOpacities.top
                : individualBorderOpacities[activeBorderSide]}%
            </div>
          </div> */}
        </div>
      </div>

      {/* Radius Mode Row */}
      <div className="border-property">
        <div className="style-label">Radius</div>
        <div className="style-controls radius-mode-controls">
          <button
            className={`radius-mode-button ${radiusMode === "all" ? "active" : ""}`}
            onClick={() => handleRadiusModeChange("all")}
            title="All Corners"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="18" height="18" rx="5" ry="5" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
          </button>
          <button
            className={`radius-mode-button ${radiusMode === "individual" ? "active" : ""}`}
            onClick={() => handleRadiusModeChange("individual")}
            title="Individual Corners"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 8v13H8" stroke="currentColor" strokeWidth="2" />
              <path d="M3 16v-13h13" stroke="currentColor" strokeWidth="2" />
              <path d="M8 3c3 0 5 2 5 5" stroke="currentColor" strokeWidth="2" />
              <path d="M16 21c-3 0-5-2-5-5" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Corner Selection Row - only show if individual mode is selected */}
      {radiusMode === "individual" && (
        <div className="border-property">
          <div className="style-label">Corner</div>
          <div className="style-controls radius-corner-controls">
            <button
              className={`radius-corner-button ${activeCorner === "topLeft" ? "active" : ""}`}
              onClick={() => setActiveCorner("topLeft")}
              title="Top Left Corner"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 8v13h13" stroke="currentColor" strokeWidth="2" />
                <path d="M8 3c3 0 5 2 5 5" stroke="currentColor" strokeWidth="2" strokeDasharray={activeCorner === "topLeft" ? "0" : "2 2"} />
              </svg>
            </button>
            <button
              className={`radius-corner-button ${activeCorner === "topRight" ? "active" : ""}`}
              onClick={() => setActiveCorner("topRight")}
              title="Top Right Corner"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 8v13h-13" stroke="currentColor" strokeWidth="2" />
                <path d="M16 3c-3 0-5 2-5 5" stroke="currentColor" strokeWidth="2" strokeDasharray={activeCorner === "topRight" ? "0" : "2 2"} />
              </svg>
            </button>
            <button
              className={`radius-corner-button ${activeCorner === "bottomRight" ? "active" : ""}`}
              onClick={() => setActiveCorner("bottomRight")}
              title="Bottom Right Corner"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 16v-13h-13" stroke="currentColor" strokeWidth="2" />
                <path d="M16 21c-3 0-5-2-5-5" stroke="currentColor" strokeWidth="2" strokeDasharray={activeCorner === "bottomRight" ? "0" : "2 2"} />
              </svg>
            </button>
            <button
              className={`radius-corner-button ${activeCorner === "bottomLeft" ? "active" : ""}`}
              onClick={() => setActiveCorner("bottomLeft")}
              title="Bottom Left Corner"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 16v-13h13" stroke="currentColor" strokeWidth="2" />
                <path d="M8 21c3 0 5-2 5-5" stroke="currentColor" strokeWidth="2" strokeDasharray={activeCorner === "bottomLeft" ? "0" : "2 2"} />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Radius Value Row */}
      <div className="border-property">
        <div className="style-label">Value</div>
        <div className="style-controls radius-controls">
          <input
            type="text"
            className="style-input"
            placeholder="0"
            value={radiusMode === "all" 
              ? selectedElement?.borders?.borderData?.allModeRadiusValue || individualBorderRadius.topLeft
              : individualBorderRadius[activeCorner]}
            onChange={(e) => 
              radiusMode === "all"
                ? handleBorderRadiusChange(e.target.value)
                : handleIndividualBorderRadiusChange(activeCorner, e.target.value)
            }
          />
          <div className="unit-dropdown">
            <select
              className="unit-select"
              value={radiusMode === "all" ? borderRadiusUnit : individualBorderRadiusUnits[activeCorner]}
              onChange={(e) => 
                radiusMode === "all"
                  ? handleBorderRadiusUnitChange(e.target.value)
                  : handleIndividualBorderRadiusUnitChange(activeCorner, e.target.value)
              }
            >
              <option value="" disabled>Unit</option>
              <option value="px">px</option>
              <option value="%">%</option>
              <option value="em">em</option>
              <option value="rem">rem</option>
              <option value="ch">ch</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Borders; 